import { computed, toRefs, ref, Ref, inject, watch } from 'vue'
import { UPDATE_MODEL_EVENT, CHANGE_EVENT, BLUR_EVENT, ON_CHANGE_EVENT, ON_BLUR_EVENT, FOCUS_EVENT, ON_FOCUS_EVENT } from './constants'
import { PROVIDE_KEY } from '@/components/form'

import i18n from '@/i18n'
import {
  required,
  minValue as minVal,
  maxValue as maxVal,
  between,
  email
} from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { formatDate } from '@/utils/date'
import { isValidPhoneNumber } from './validators'

const VALIDATE_CLS = 'validatable'
/**
 * @description Props phục vụ validate
 * @author PHNam
 */
const validate = {
  props: {
    modelValue: {
      type: [String, Number, Array],
      default: ''
    },

    /**
     * @description Cho phép để trống
     */
    allowBlank: {
      type: Boolean,
      default: true
    },

    /**
     * @description Giá trị nhỏ nhất
     */
    minValue: {
      type: [Number, Date]
    },

    /**
     * @description Giá trị lớn nhất
     */
    maxValue: {
      type: [Number, Date]
    },

    /**
     * @description Địa chỉ email
     */
    isEmail: {
      type: Boolean,
      default: false
    },

    /**
     * @description Số điện thoại
     */
    isPhoneNumber: {
      type: Boolean,
      default: false
    }
  }
}

/**
 * @description Utility validate cho text-field, combobox, ...
 * @param {*} props Props của component ở setup
 * @param {*} context context của component ở setup
 * @returns Đối tượng validate
 * @author PHNam
 */
const useValidate = function (props: Record<string, any>, { emit: $emit }: any) {
  const validated: Ref<boolean> = inject(PROVIDE_KEY, ref(true)) as Ref<boolean> || ref(true)

  const refProps = toRefs(props)
  const { modelValue } = refProps
  const validations = getValidations(refProps)
  const rules = computed(() => ({
    modelValue: {
      ...validations
    }
  }))
  const v$ = useVuelidate(rules, { modelValue }) // Ref validate của Vuelidate

  // Methods
  const touch = function () {
    v$.value.$touch()
  }

  const isValid = function () {
    touch()

    return !hasError.value
  }
  const onChange = function (val: any) {
    touch()
    $emit(CHANGE_EVENT, val)
  }

  const onBlur = function (event: any) {
    touch()
    $emit(BLUR_EVENT, event)
  }

  const onFocus = (event: any) => {
    // reset()
    $emit(FOCUS_EVENT, event)
  }

  const customError = ref(false)
  const customErrorMessage = ref('')

  // Computed property
  const value = computed({
    get () {
      return modelValue.value
    },
    set (newVal) {
      $emit(UPDATE_MODEL_EVENT, newVal)
    }
  })
  const validateCls = computed(() => VALIDATE_CLS)
  const hasError = computed(() => {
    return validated.value && (customError.value || v$.value.$error)
  })

  const reset = function () {
    v$.value.$reset()
    value.value = null
  }
  const errorMessage = computed(() => !validated.value ? '' : customErrorMessage.value || getErrorMessage(v$.value.$errors))
  const setCustomError = (errorMsg: string) => {
    customError.value = true
    customErrorMessage.value = errorMsg
  }
  const clearCustomError = () => {
    customError.value = false
    customErrorMessage.value = ''
  }

  // Others
  const validateEvents = {
    [ON_CHANGE_EVENT]: onChange,
    [ON_BLUR_EVENT]: onBlur,
    [ON_FOCUS_EVENT]: onFocus
  }
  const methods = { isValid, touch, reset, setCustomError, clearCustomError }
  const computedProps = { v$, value, hasError, errorMessage, validateCls, validated }
  const others = { validateEvents }

  return {
    methods,
    computedProps,
    others
  }
}

/**
 * @description Trả về rule validate
 * @param {*} props Props của component ở setup
 * @returns
 * @author PHNam
 */
const getValidations = function (props: Record<string, any>) {
  const { allowBlank, minValue, maxValue, isEmail, isPhoneNumber } = props
  const validations: Record<string, any> = {}

  if (!allowBlank.value) {
    validations.required = required
  }

  if (minValue?.value && maxValue?.value && minValue.value <= maxValue.value) {
    validations.between = between(minValue.value, maxValue.value)
  } else {
    if (minValue?.value) {
      validations.minValue = minVal(minValue?.value)
    } else if (maxValue?.value) {
      validations.maxValue = maxVal(maxValue.value)
    }
  }

  if (isEmail.value) {
    validations.email = email
  }

  if (isPhoneNumber.value) {
    validations.phoneNumber = isValidPhoneNumber
  }

  return validations
}

/**
 * @description Lấy message error theo lỗi tương ứng
 * @param {Array} errors
 * @returns
 * @author PHNam
 */
const getErrorMessage = function (errors: any[]) {
  const $t = i18n?.global?.t
  const error = errors?.[0]
  let message = ''

  switch (error?.$validator) {
    case 'required':
      message = $t('Validate.FieldIsNotAllowEmpty')
      break
    case 'email':
      message = $t('Validate.InvalidEmailAddress')
      break
    case 'phoneNumber':
      message = $t('Validate.InvalidPhoneNumber')
      break
    case 'minValue':
      {
        const minVal = error.$params.min
        const isDate = typeof minVal === 'object'
        const minValue = `${isDate ? $t('Validate.Date') : ''}${
            isDate ? formatDate(minVal, 'dd-mm-yyyy') : minVal
          }`

        message = $t('Validate.FieldValueIsLessThanMinValue', {
          minValue: minValue
        })
      }
      break
    case 'maxValue':
      {
        const maxVal = error.$params.max
        const isDate = typeof maxVal === 'object'
        const maxValue = `${isDate ? $t('Validate.Date') : ''}${
            isDate ? formatDate(maxVal, 'dd-mm-yyyy') : maxVal
          }`

        message = $t('Validate.FieldValueIsGreaterThanMaxValue', {
          maxValue: maxValue
        })
      }
      break
    case 'between':
      {
        const minVal = error.$params.min
        const maxVal = error.$params.max
        const isDate = typeof minVal === 'object'
        const minValue = `${isDate ? $t('Validate.Date') : ''}${
            isDate ? formatDate(minVal, 'dd-mm-yyyy') : minVal
          }`
        const maxValue = `${isDate ? $t('Validate.Date') : ''}${
            isDate ? formatDate(maxVal, 'dd-mm-yyyy') : maxVal
          }`

        message = $t('Validate.FieldValueIsNotBetweenMinAndMaxValue', {
          minValue: minValue,
          maxValue: maxValue
        })
      }
      break
  }

  return message
}

/**
 * @description Destructure đối tượng validate
 * @param {*} validationObject Đối tượng validate trả ra từ hàm useValidate
 * @returns Các props phục vụ validate
 * @author PHNam
 */
const getValidationProps = function (validationObject: Record<string, any>) {
  const { computedProps, methods, others } = validationObject
  const { v$, value, hasError, errorMessage, validateCls, validated } = computedProps
  const { isValid, touch, reset, setCustomError, clearCustomError } = methods
  const { validateEvents } = others

  return {
    v$,
    value,
    hasError,
    errorMessage,
    ...validateEvents,
    setCustomError,
    clearCustomError,
    isValid,
    touch,
    reset,
    validateCls,
    validated
  }
}

export { validate, useValidate, getValidationProps, VALIDATE_CLS }
